import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginState } from "../../slices/loginSlice";
import { isEmpty } from "lodash";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { LOGIN } from "../../constant/endpoint";
import { axiosRequest } from "../../utility/axios";
import styles from "./login.module.css";

// ════════════════════════════════════════════════════════════════════════════
// PUZZLE 1 — CARD SUIT MEMORY
// ════════════════════════════════════════════════════════════════════════════
const SUITS  = ["♠", "♥", "♦", "♣"];
const IS_RED = { "♠": false, "♥": true, "♦": true, "♣": false };
const randomSequence = () =>
  Array.from({ length: 4 }, () => SUITS[Math.floor(Math.random() * 4)]);

const CardPuzzle = ({ phase, sequence, userInput, onSuitClick }) => {
  const peekCardCls = (i) => {
    if (phase === "success") return `${styles.peekCard} ${styles.peekSuccess}`;
    if (phase === "error")   return `${styles.peekCard} ${styles.peekError}`;
    if (phase === "peek")    return `${styles.peekCard} ${styles.peekLit}`;
    if (i < userInput.length) return `${styles.peekCard} ${styles.peekFilled}`;
    return styles.peekCard;
  };
  const showSuit = (i) =>
    phase === "peek" || phase === "success" ||
    (["input", "error"].includes(phase) && i < userInput.length);

  return (
    <>
      <div className={styles.peekRow}>
        {sequence.map((suit, i) => (
          <div key={i} className={peekCardCls(i)}
            style={{ color: showSuit(i) ? (IS_RED[suit] ? "#ff6b6b" : "#a0ffc0") : "rgba(255,255,255,0.12)" }}>
            {showSuit(i) ? suit : "?"}
          </div>
        ))}
      </div>
      <div className={styles.dotRow}>
        {sequence.map((_, i) => (
          <span key={i} className={`${styles.dot}
            ${phase === "success" ? styles.dotSuccess : ""}
            ${phase === "error"   ? styles.dotError   : ""}
            ${phase === "input" && i < userInput.length ? styles.dotFilled : ""}`} />
        ))}
      </div>
      <div className={styles.suitBtnRow}>
        {SUITS.map((suit) => (
          <button key={suit} className={styles.suitBtn}
            style={{ color: IS_RED[suit] ? "#ff6b6b" : "#a0ffc0" }}
            onClick={() => onSuitClick(suit)} disabled={phase !== "input"}>
            {suit}
          </button>
        ))}
      </div>
    </>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PUZZLE 2 — PATTERN LOCK
// ════════════════════════════════════════════════════════════════════════════
const GRID = 3, CELL = 80, PAT_LEN = 4;

const generatePattern = () => {
  const pool = Array.from({ length: 9 }, (_, i) => i);
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, PAT_LEN);
};

const dotCenter = (idx) => ({
  x: (idx % GRID) * CELL + CELL / 2,
  y: Math.floor(idx / GRID) * CELL + CELL / 2,
});

const PatternLock = ({ phase, pattern, userPattern, onDotClick }) => {
  const activeSeq = (phase === "peek" || phase === "success") ? pattern : userPattern;
  const isError   = phase === "error";
  const isSuccess = phase === "success";
  const lineColor = isError   ? "rgba(255,60,60,0.6)"
                  : isSuccess ? "rgba(0,255,120,0.85)"
                  :             "rgba(0,255,120,0.55)";

  const dotCls = (idx) => {
    if (!activeSeq.includes(idx)) return styles.lockDot;
    if (phase === "peek")  return `${styles.lockDot} ${styles.lockDotPeek}`;
    if (isSuccess)         return `${styles.lockDot} ${styles.lockDotSuccess}`;
    if (isError)           return `${styles.lockDot} ${styles.lockDotError}`;
    return `${styles.lockDot} ${styles.lockDotActive}`;
  };

  return (
    <div className={styles.lockGrid}>
      <svg className={styles.lockSvg} viewBox={`0 0 ${GRID * CELL} ${GRID * CELL}`} xmlns="http://www.w3.org/2000/svg">
        {activeSeq.slice(0, -1).map((from, i) => {
          const to = activeSeq[i + 1], a = dotCenter(from), b = dotCenter(to);
          return <line key={`${from}-${to}-${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
            stroke={lineColor} strokeWidth="2.5" strokeLinecap="round" />;
        })}
      </svg>
      {Array.from({ length: 9 }, (_, i) => {
        const seqPos = activeSeq.indexOf(i);
        return (
          <div key={i} className={dotCls(i)} onClick={() => onDotClick(i)}>
            <div className={styles.lockDotCore}>
              {seqPos !== -1 && <span className={styles.lockDotNum}>{seqPos + 1}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PUZZLE 3 — COLOR SIMON SAYS
// ════════════════════════════════════════════════════════════════════════════
const SIMON_COLORS = [
  { id: 0, bg: "#7f1d1d", active: "#ef4444", glow: "rgba(239,68,68,0.75)",   label: "RED"   },
  { id: 1, bg: "#1e3a5f", active: "#3b82f6", glow: "rgba(59,130,246,0.75)",  label: "BLUE"  },
  { id: 2, bg: "#14532d", active: "#22c55e", glow: "rgba(34,197,94,0.75)",   label: "GREEN" },
  { id: 3, bg: "#713f12", active: "#eab308", glow: "rgba(234,179,8,0.75)",   label: "GOLD"  },
];

// unique permutation — each color appears exactly once
const randomColorSeq = () => {
  const seq = [0, 1, 2, 3];
  for (let i = seq.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [seq[i], seq[j]] = [seq[j], seq[i]];
  }
  return seq;
};

const SimonPuzzle = ({ phase, colorSeq, userColors, onColorClick }) => {
  const seqPos = {};
  colorSeq.forEach((cId, i) => { seqPos[cId] = i + 1; });
  const lit = (cId) => phase === "peek" || phase === "success" || userColors.includes(cId);

  const padCls = (cId) => {
    if (phase === "peek")                         return `${styles.simonPad} ${styles.simonPadPeek}`;
    if (phase === "success")                      return `${styles.simonPad} ${styles.simonPadSuccess}`;
    if (phase === "error" && userColors.includes(cId)) return `${styles.simonPad} ${styles.simonPadError}`;
    if (userColors.includes(cId))                 return `${styles.simonPad} ${styles.simonPadActive}`;
    return styles.simonPad;
  };

  return (
    <div className={styles.simonGrid}>
      {SIMON_COLORS.map((c) => (
        <div key={c.id} className={padCls(c.id)}
          style={{
            background: lit(c.id) ? c.active : c.bg,
            boxShadow: lit(c.id) ? `0 0 24px ${c.glow}, inset 0 0 16px rgba(255,255,255,0.12)` : "none",
          }}
          onClick={() => onColorClick(c.id)}>
          {lit(c.id) && <span className={styles.simonNum}>{seqPos[c.id]}</span>}
          {!lit(c.id) && <span className={styles.simonLabel}>{c.label}</span>}
        </div>
      ))}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PUZZLE 4 — DIAL COMBINATION LOCK
// ════════════════════════════════════════════════════════════════════════════
const randomDialTarget = () => Array.from({ length: 3 }, () => Math.floor(Math.random() * 10));
const randomDialValues = () => Array.from({ length: 3 }, () => Math.floor(Math.random() * 10));

const DialPuzzle = ({ phase, dialTarget, dialValues, onDial }) => {
  const isInput = phase === "input";

  const displayCls = (i) => {
    const matched = dialValues[i] === dialTarget[i];
    if (phase === "peek")    return `${styles.dialDisplay} ${styles.dialDisplayPeek}`;
    if (phase === "success") return `${styles.dialDisplay} ${styles.dialDisplaySuccess}`;
    if (isInput && matched)  return `${styles.dialDisplay} ${styles.dialDisplayMatch}`;
    return styles.dialDisplay;
  };

  return (
    <div className={styles.dialRow}>
      {dialTarget.map((target, i) => (
        <React.Fragment key={i}>
          <div className={styles.dialWrap}>
            <button className={styles.dialBtn} disabled={!isInput} onClick={() => onDial(i, 1)}>▲</button>
            <div className={displayCls(i)}>
              {phase === "peek" ? target : dialValues[i]}
            </div>
            <button className={styles.dialBtn} disabled={!isInput} onClick={() => onDial(i, -1)}>▼</button>
            <span className={styles.dialLabel}>D{i + 1}</span>
          </div>
          {i < 2 && <span className={styles.dialSep}>—</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// SHARED
// ════════════════════════════════════════════════════════════════════════════
const BG_SYMBOLS = ["◆", "♠", "●", "♥", "▲", "♦", "■", "♣", "✦", "★"];
const PARTICLES  = Array.from({ length: 22 }, (_, i) => ({
  id: i, symbol: BG_SYMBOLS[i % BG_SYMBOLS.length],
  left: Math.random() * 100, delay: Math.random() * 14,
  duration: 10 + Math.random() * 14, size: 10 + Math.random() * 18,
  hue: Math.random() > 0.5 ? "rgba(0,255,120,0.45)" : "rgba(120,80,255,0.35)",
}));

const THOUGHTS = [
  "Patience is the sharpest tool.",
  "The cards never lie…",
  "Every lock has its key.",
  "Fortune favors the patient hand.",
  "The pattern reveals the path.",
  "Strategy beats luck, every time.",
  "Precision beats hesitation.",
  "Discipline opens every door.",
  "Think before you move.",
  "Calculated risk is not a gamble.",
  "Focus is the master key.",
  "The right combination unlocks everything.",
];

const PUZZLE_TYPES = ["card", "pattern", "simon", "dial"];

// LOCKDOWN — mirrors the backend's LOCKDOWN_MODE. Shows a generic 404 instead of the
// puzzle/login form when true. Flip back to true (and redeploy) to lock the login screen.
const LOCKDOWN = false;

const AnimatedLogo = () => (
  <div className={styles.animLogo}>
    <div className={styles.animRing}>
      <span className={`${styles.orbitSuit} ${styles.orbitTop}`}>◆</span>
      <span className={`${styles.orbitSuit} ${styles.orbitRight}`} style={{ color: "#ff6b6b" }}>♥</span>
      <span className={`${styles.orbitSuit} ${styles.orbitBottom}`} style={{ color: "#a78bfa" }}>▲</span>
      <span className={`${styles.orbitSuit} ${styles.orbitLeft}`} style={{ color: "#fbbf24" }}>♠</span>
    </div>
    <div className={styles.animCenter}>✦</div>
    <div className={styles.animGlow} />
  </div>
);

const RandomThought = () => {
  const [idx, setIdx]         = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const iv = setInterval(() => {
      setVisible(false);
      setTimeout(() => { setIdx((i) => (i + 1) % THOUGHTS.length); setVisible(true); }, 500);
    }, 3800);
    return () => clearInterval(iv);
  }, []);
  return (
    <p className={`${styles.thought} ${visible ? styles.thoughtIn : styles.thoughtOut}`}>
      "{THOUGHTS[idx]}"
    </p>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN LOGIN
// ════════════════════════════════════════════════════════════════════════════
const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const login    = useSelector((state) => state.loginSlice);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);

  // pick one puzzle randomly on mount
  const [puzzleType] = useState(
    () => PUZZLE_TYPES[Math.floor(Math.random() * PUZZLE_TYPES.length)]
  );

  // shared phase machine
  const [phase, setPhase] = useState("peek");

  // puzzle 1 state
  const [sequence, setSequence]   = useState(randomSequence);
  const [userInput, setUserInput] = useState([]);

  // puzzle 2 state
  const [pattern, setPattern]         = useState(generatePattern);
  const [userPattern, setUserPattern] = useState([]);

  // puzzle 3 state
  const [colorSeq, setColorSeq]     = useState(randomColorSeq);
  const [userColors, setUserColors] = useState([]);

  // puzzle 4 state
  const [dialTarget, setDialTarget] = useState(randomDialTarget);
  const [dialValues, setDialValues] = useState(randomDialValues);

  // peek → input (dial gets 2.5 s, others 2.8 s)
  useEffect(() => {
    if (phase !== "peek") return;
    const dur = puzzleType === "dial" ? 2500 : 2800;
    const t = setTimeout(() => setPhase("input"), dur);
    return () => clearTimeout(t);
  }, [phase, puzzleType]);

  // error → reset & new round (dial has no error state)
  useEffect(() => {
    if (phase !== "error") return;
    const t = setTimeout(() => {
      if (puzzleType === "card")    { setSequence(randomSequence()); setUserInput([]); }
      if (puzzleType === "pattern") { setPattern(generatePattern()); setUserPattern([]); }
      if (puzzleType === "simon")   { setColorSeq(randomColorSeq()); setUserColors([]); }
      setPhase("peek");
    }, 1400);
    return () => clearTimeout(t);
  }, [phase, puzzleType]);

  // success → solved
  useEffect(() => {
    if (phase !== "success") return;
    const t = setTimeout(() => setPhase("solved"), 1200);
    return () => clearTimeout(t);
  }, [phase]);

  // ── handlers ─────────────────────────────────────────────────────────────
  const handleSuitClick = (suit) => {
    if (phase !== "input") return;
    const next = [...userInput, suit];
    const idx  = next.length - 1;
    if (next[idx] !== sequence[idx]) { setUserInput(next); setPhase("error"); return; }
    setUserInput(next);
    if (next.length === sequence.length) setPhase("success");
  };

  const handleDotClick = (idx) => {
    if (phase !== "input" || userPattern.includes(idx)) return;
    const next = [...userPattern, idx];
    const pos  = next.length - 1;
    if (next[pos] !== pattern[pos]) { setUserPattern(next); setPhase("error"); return; }
    setUserPattern(next);
    if (next.length === pattern.length) setPhase("success");
  };

  const handleColorClick = (colorId) => {
    if (phase !== "input") return;
    const next = [...userColors, colorId];
    const pos  = next.length - 1;
    if (next[pos] !== colorSeq[pos]) { setUserColors(next); setPhase("error"); return; }
    setUserColors(next);
    if (next.length === colorSeq.length) setPhase("success");
  };

  const handleDial = (dialIndex, dir) => {
    if (phase !== "input") return;
    const next = [...dialValues];
    next[dialIndex] = (next[dialIndex] + dir + 10) % 10;
    setDialValues(next);
    if (next.every((v, i) => v === dialTarget[i])) setPhase("success");
  };

  // ── login ─────────────────────────────────────────────────────────────────
  const setLogin = (e) =>
    dispatch(loginState({ name: [e.target.name], value: e.target.value }));

  const showAlert = (type, title) =>
    Swal.fire({ position: "top-end", icon: type, title, showConfirmButton: false, timer: 3000 });

  const validation = () => {
    if (isEmpty(login.email))    { showAlert("error", "Please enter your email.");    return false; }
    if (isEmpty(login.password)) { showAlert("error", "Please enter your password."); return false; }
    return true;
  };

  const loginHandle = async () => {
    if (!validation()) return;
    setLoading(true);
    try {
      const res = await axiosRequest("POST", LOGIN, "", login);
      if (res?.status === "success") {
        dispatch(loginState({ name: ["token"], value: res.data.token }));
        localStorage.setItem("userToken", res.data.token);
        navigate("/shift-management");
      } else {
        showAlert("error", res?.response?.data?.error || "Login failed!");
      }
    } catch { showAlert("error", "Something went wrong!"); }
    finally  { setLoading(false); }
  };

  const handleKey = (e) => { if (e.key === "Enter") loginHandle(); };

  // ── labels ────────────────────────────────────────────────────────────────
  const statusLabel = () => {
    if (phase === "success") return "✦ Access Granted! ✦";
    const map = {
      card:    { peek: "Memorise the sequence…",    input: `Input: ${userInput.length} / ${sequence.length}`,  error: "Wrong! New code incoming…"    },
      pattern: { peek: "Memorise the pattern…",     input: `Draw: ${userPattern.length} / ${pattern.length}`,  error: "Wrong path! New pattern…"     },
      simon:   { peek: "Memorise the colour order…",input: `Colours: ${userColors.length} / ${colorSeq.length}`, error: "Wrong colour! Try again…"  },
      dial:    { peek: "Remember this combination…",input: "Spin the dials to match" },
    };
    return map[puzzleType]?.[phase] || "";
  };

  const hintText = () => {
    if (phase === "success") return puzzleType === "dial" ? "Combination cracked! Unlocking…" : "Seal broken. Unlocking…";
    const map = {
      card:    { peek: "Cards will hide — repeat the order shown", input: "Tap suits in the exact order shown", error: "Incorrect — new sequence being dealt…"     },
      pattern: { peek: "Path will hide — tap the dots in order",   input: "Tap dots in the exact order shown", error: "Incorrect path — watch the new pattern…"   },
      simon:   { peek: "Remember which number is on each colour",  input: "Tap colours in order 1 → 2 → 3 → 4", error: "Wrong! A new colour order is coming…"    },
      dial:    { peek: "Memorise the 3-digit code",                input: "Use ▲▼ to set each dial to the correct digit"                                           },
    };
    return map[puzzleType]?.[phase] || "";
  };

  // ── render ────────────────────────────────────────────────────────────────
  if (LOCKDOWN) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", background: "#fff",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: "#222",
      }}>
        <h1 style={{ fontSize: 28, fontWeight: 500, margin: 0 }}>404</h1>
        <p style={{ fontSize: 16, marginTop: 8, color: "#555" }}>Page Not Found</p>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.scanline} />
      <div className={styles.grid} />

      <div className={styles.particleLayer}>
        {PARTICLES.map((p) => (
          <span key={p.id} className={styles.particle} style={{
            left: `${p.left}%`, fontSize: `${p.size}px`, color: p.hue,
            animationDuration: `${p.duration}s`, animationDelay: `${p.delay}s`,
          }}>{p.symbol}</span>
        ))}
      </div>

      {/* PUZZLE CARD */}
      {phase !== "solved" && (
        <div className={`${styles.card}
          ${phase === "error"   ? styles.shake       : ""}
          ${phase === "success" ? styles.glowSuccess : ""}
        `}>
          <AnimatedLogo />
          <h1 className={styles.title}>Crack the Seal</h1>
          <p className={styles.subtitle}>{statusLabel()}</p>
          <RandomThought />
          <hr className={styles.divider} />

          {puzzleType === "card"    && <CardPuzzle    phase={phase} sequence={sequence}     userInput={userInput}     onSuitClick={handleSuitClick} />}
          {puzzleType === "pattern" && <PatternLock   phase={phase} pattern={pattern}       userPattern={userPattern} onDotClick={handleDotClick}   />}
          {puzzleType === "simon"   && <SimonPuzzle   phase={phase} colorSeq={colorSeq}     userColors={userColors}   onColorClick={handleColorClick}/>}
          {puzzleType === "dial"    && <DialPuzzle    phase={phase} dialTarget={dialTarget}  dialValues={dialValues}   onDial={handleDial}           />}

          <p className={styles.hintText}>{hintText()}</p>
        </div>
      )}

      {/* LOGIN CARD */}
      {phase === "solved" && (
        <div className={`${styles.card} ${styles.loginReveal}`}>
          <AnimatedLogo />
          <RandomThought />
          <hr className={styles.divider} />

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Email Address</label>
            <input name="email" type="email" placeholder="admin@club.com"
              autoComplete="username" className={styles.input}
              onChange={setLogin} onKeyDown={handleKey} />
            <span className={styles.inputIcon}>✉</span>
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Password</label>
            <input name="password" type={showPassword ? "text" : "password"}
              placeholder="••••••••" autoComplete="current-password"
              className={styles.input} style={{ paddingRight: "44px" }}
              onChange={setLogin} onKeyDown={handleKey} />
            <button type="button" className={styles.eyeToggle}
              onClick={() => setShowPassword((v) => !v)} tabIndex={-1}>
              {showPassword ? "🙈" : "👁"}
            </button>
          </div>

          <button className={styles.submitBtn} onClick={loginHandle} disabled={loading}>
            {loading && <span className={styles.spinner} />}
            {loading ? "Verifying…" : "Unlock Access"}
          </button>

          <div className={styles.suitRow}>
            <span className={styles.suitBlack}>♠</span>
            <span className={styles.suitRed}>♥</span>
            <span className={styles.suitRed}>♦</span>
            <span className={styles.suitBlack}>♣</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
